<script lang="ts">
import { Image } from '@builder.io/sdk-svelte';

interface Props {
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    description?: string;
    backgroundColour?: string;
    txtColour?: string;
    people?: Array<{
        image: {
            altText: string;
            image: string;
        };
        image2: {
            altText: string;
            image: string;
        };
        role: string;
        name: string;
        linkedin?: string;
    }>;
    hideBlock?: boolean;
}

const {
    title = '',
    headingLevel = 'h2',
    titleSize = 'h2',
    description = '',
    backgroundColour = '',
    txtColour = '',
    people = [],
    hideBlock = false,
}: Props = $props();

const hasBackgroundColour = $derived(backgroundColour.trim().length > 0);
const titleSizeClassName = $derived(`title--${titleSize}`);
</script>

{#if !hideBlock}
    <div
        class="leadership"
        class:leadership--has-background-colour={hasBackgroundColour}
        style:--bg-colour={backgroundColour || undefined}
        style:--txt-colour={txtColour || undefined}
    >
        <div class="intro">
            {#if title}
                <svelte:element this={headingLevel} class="title {titleSizeClassName}">{title}</svelte:element>
            {/if}

            {#if description}
                <p class="description">{description}</p>
            {/if}
        </div>

        {#each people as person, index (index)}
            <div class="person" data-aos="fade-up" tabindex="0" role="button"
         aria-label="View alternate headshot for {person.name}">
                <Image {...person.image} className="headshot" />
                <Image {...person.image2} className="headshot-rollover" />
                <span class="role">{person.role}</span>
                <span class="name">{person.name}</span>
            </div>
        {/each}
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use '/src/scss/global-constants/fonts';
    @use 'sass-mq/mq' as *;

    .leadership {
        background-color: var(--bg-colour, transparent);
        display: grid;
        gap: 2.4rem;
        grid-template-columns: repeat(2, 1fr);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        @include mq($from: tablet) {
            grid-template-columns: repeat(3, 1fr);
            row-gap: 4rem;
        }
    }

    .leadership--has-background-colour {
        padding-block: var(--layout-pagegrid-margin);
    }

    .intro {
        @include mq($until: tablet) {
            grid-column: 1 / -1;
        }
    }

    .title {
        color: var(--txt-colour, var(--colour-dreamless-sleep));
        margin-bottom: 1.6rem;
        max-width: 12ch;
        text-wrap: balance;

        @include mq($from: tablet) {
            margin-bottom: 2.4rem;
        }

        &--h1   { @include typography.display-xl; }
        &--h1-5 { @include typography.display-hero; }
        &--h2   { @include typography.display-lg; }
        &--h3   { @include typography.display-md; }
        &--h4   { @include typography.heading-lg; }
        &--h5   { @include typography.heading-md; }
    }

    .description {
        color: var(--txt-colour, var(--colour-dreamless-sleep));
        margin-bottom: 1.2rem;
        @include typography.body-base;

        @include mq($from: tablet) {
            margin-bottom: 0;
        }
    }

    .person {
        position: relative;
    }

    .leadership :global(.headshot) {
        margin-bottom: 0.9rem;
        width: 100%;
    }

    .name {
        color: var(--txt-colour, var(--colour-dreamless-sleep));
        display: block;
        @include typography.label-name;
    }

    .role {
        color: var(--txt-colour, var(--colour-squant));
        display: block;
        margin-bottom: 0.5rem;
        text-transform: uppercase;
        @include typography.label-meta;
    }

    :global(.headshot-rollover) {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        transition: opacity 0.3s ease;
    }

    .person:hover :global(.headshot-rollover),
    .person:focus :global(.headshot-rollover) {
        opacity: 1;
    }
</style>
